/** @format */

import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Fade,
} from "@mui/material";
import {
    ArrowForward,
    CheckCircleOutline,
    AutoAwesome,
    Bolt,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { colors } from "../../utility/types/constant";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";
import axios from "axios";
import { Footer, SEO } from "../allComponents/AllComponents";
import { useTranslation } from "react-i18next";

// ── Styled components ───────────────────────────────────────────────────────

const PageWrapper = styled("div")({
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "linear-gradient(135deg, #0d1636 0%, #1a2a6c 60%, #243590 100%)",
    position: "relative",
    "&::before": {
        content: '""',
        position: "fixed",
        inset: 0,
        background:
            "radial-gradient(ellipse at 72% 8%, rgba(255,204,0,0.11) 0%, transparent 48%), radial-gradient(ellipse at 12% 88%, rgba(30,193,149,0.09) 0%, transparent 44%)",
        pointerEvents: "none",
        zIndex: 0,
    },
});

const SplitContainer = styled("div")(({ theme }) => ({
    flex: 1,
    display: "flex",
    maxWidth: "1200px",
    width: "100%",
    margin: "0 auto",
    padding: "64px 48px 48px",
    gap: "56px",
    alignItems: "flex-start",
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("lg")]: {
        padding: "56px 36px 40px",
        gap: "40px",
    },
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        padding: "36px 24px 28px",
        gap: "28px",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "28px 16px 20px",
        gap: "22px",
    },
}));

const HeroPane = styled("div")(({ theme }) => ({
    flex: "0 0 400px",
    position: "sticky",
    top: "88px",
    [theme.breakpoints.down("lg")]: {
        flex: "0 0 340px",
    },
    [theme.breakpoints.down("md")]: {
        position: "static",
        flex: "none",
        width: "100%",
    },
}));

const FormPane = styled("div")(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    paddingBottom: "40px",
    [theme.breakpoints.down("md")]: {
        width: "100%",
        paddingBottom: "24px",
    },
}));

const BadgeChip = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(255, 204, 0, 0.15)",
    color: "#ffcc00",
    border: "1px solid rgba(255, 204, 0, 0.30)",
    borderRadius: "100px",
    padding: "5px 14px",
    fontSize: "0.7rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    marginBottom: "20px",
});

const HeroTitle = styled(Typography)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.65rem, 3vw, 2.5rem)",
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1.13,
    letterSpacing: "-0.02em",
    marginBottom: "18px",
    [theme.breakpoints.down("md")]: {
        fontSize: "clamp(1.55rem, 5.5vw, 2.1rem)",
    },
}));

const HeroSub = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "clamp(0.87rem, 1.4vw, 0.98rem)",
    color: "rgba(255,255,255,0.65)",
    lineHeight: 1.8,
    whiteSpace: "pre-line",
    marginBottom: "26px",
});

const TimeBadge = styled("div")({
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.13)",
    borderRadius: "100px",
    padding: "7px 18px",
    fontSize: "0.8rem",
    fontFamily: "'Inter', sans-serif",
    color: "rgba(255,255,255,0.55)",
    fontWeight: 500,
    marginBottom: "32px",
});

const TrustList = styled("div")({
    display: "flex",
    flexDirection: "column",
    gap: "13px",
    paddingTop: "28px",
    borderTop: "1px solid rgba(255,255,255,0.09)",
});

const TrustItem = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.87rem",
    color: "rgba(255,255,255,0.58)",
    lineHeight: 1.55,
});

const TrustDot = styled("span")({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    borderRadius: "50%",
    background: "rgba(30,193,149,0.15)",
    border: "1px solid rgba(30,193,149,0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "1px",
    fontSize: "0.7rem",
    color: colors.mainSecondary,
    flexShrink: 0,
});

const PainSection = styled("div")({
    marginTop: "28px",
    padding: "20px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "14px",
});

const PainHeader = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "rgba(255,204,0,0.75)",
    marginBottom: "12px",
});

const PainItem = styled("div")({
    display: "flex",
    alignItems: "flex-start",
    gap: "9px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.86rem",
    color: "rgba(255,255,255,0.72)",
    lineHeight: 1.55,
    marginBottom: "8px",
    "&:last-of-type": { marginBottom: 0 },
});

const PainDot = styled("span")({
    width: "5px",
    height: "5px",
    minWidth: "5px",
    borderRadius: "50%",
    background: "rgba(255,100,100,0.65)",
    marginTop: "7px",
    flexShrink: 0,
});

const PainFootnote = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.38)",
    marginTop: "14px",
    lineHeight: 1.6,
    borderTop: "1px solid rgba(255,255,255,0.07)",
    paddingTop: "12px",
});

const Card = styled("div")(({ theme }) => ({
    background: "#ffffff",
    borderRadius: "18px",
    padding: "40px 36px",
    boxShadow:
        "0 1px 3px rgba(4,13,69,0.04), 0 4px 20px rgba(4,13,69,0.08), 0 0 0 1px rgba(4,13,69,0.04)",
    [theme.breakpoints.down("sm")]: {
        padding: "28px 20px",
    },
}));

const OptionalCard = styled("div")(({ theme }) => ({
    background: "rgba(30,193,149,0.04)",
    border: "1.5px dashed rgba(30,193,149,0.30)",
    borderRadius: "18px",
    padding: "32px 36px",
    marginTop: "18px",
    [theme.breakpoints.down("sm")]: {
        padding: "24px 20px",
    },
}));

const QuestionLabel = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.97rem",
    fontWeight: 700,
    color: colors.primary,
    marginBottom: "12px",
    lineHeight: 1.4,
});

const RequiredStar = styled("span")({
    color: "#ef4444",
    marginLeft: "3px",
});

const HintText = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.76rem",
    color: "#9ca3af",
    marginTop: "-8px",
    marginBottom: "12px",
    lineHeight: 1.5,
});

const Divider = styled("div")({
    height: "1px",
    background:
        "linear-gradient(90deg, transparent, #e5e7eb 20%, #e5e7eb 80%, transparent)",
    margin: "30px 0",
});

// ── Option cards (radio) ──────────────────────────────────────────────────────

const OptionCard = styled("button")<{ selected: boolean }>(({ selected }) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    textAlign: "left",
    padding: "13px 16px",
    borderRadius: "11px",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #e5e7eb",
    background: selected ? "rgba(30,193,149,0.07)" : "#fafafa",
    cursor: "pointer",
    marginBottom: "9px",
    transition: "all 0.18s ease",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.93rem",
    fontWeight: selected ? 600 : 400,
    color: selected ? colors.primary : "#374151",
    outline: "none",
    "&:hover": {
        border: `2px solid ${colors.mainSecondary}`,
        background: "rgba(30,193,149,0.05)",
    },
}));

const RadioDot = styled("span")<{ selected: boolean }>(({ selected }) => ({
    width: "18px",
    height: "18px",
    minWidth: "18px",
    borderRadius: "50%",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #d1d5db",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    marginRight: "12px",
    flexShrink: 0,
    "&::after": selected
        ? { content: '""', width: "8px", height: "8px", borderRadius: "50%", background: colors.mainSecondary }
        : {},
}));

// ── Checkbox cards ────────────────────────────────────────────────────────────

const CheckCard = styled("button")<{ selected: boolean }>(({ selected }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    textAlign: "left",
    padding: "12px 15px",
    borderRadius: "11px",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #e5e7eb",
    background: selected ? "rgba(30,193,149,0.07)" : "#fafafa",
    cursor: "pointer",
    marginBottom: "8px",
    transition: "all 0.18s ease",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.92rem",
    fontWeight: selected ? 600 : 400,
    color: selected ? colors.primary : "#374151",
    outline: "none",
    "&:hover": {
        border: `2px solid ${colors.mainSecondary}`,
        background: "rgba(30,193,149,0.05)",
    },
}));

const CheckBox = styled("span")<{ selected: boolean }>(({ selected }) => ({
    width: "19px",
    height: "19px",
    minWidth: "19px",
    borderRadius: "5px",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #d1d5db",
    background: selected ? colors.mainSecondary : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
    flexShrink: 0,
}));

// ── Score grid (1–10) ─────────────────────────────────────────────────────────

const ScoreGrid = styled("div")(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(10, 1fr)",
    gap: "6px",
    marginBottom: "6px",
    [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: "8px",
    },
}));

const ScoreBtn = styled("button")<{ selected: boolean }>(({ selected }) => ({
    aspectRatio: "1",
    borderRadius: "10px",
    border: selected ? `2px solid ${colors.mainSecondary}` : "2px solid #e5e7eb",
    background: selected
        ? `linear-gradient(135deg, ${colors.mainSecondary}, #17a882)`
        : "#fafafa",
    color: selected ? "#fff" : "#374151",
    cursor: "pointer",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: selected ? 700 : 500,
    fontSize: "clamp(0.82rem, 1.5vw, 0.95rem)",
    transition: "all 0.18s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    outline: "none",
    boxShadow: selected ? "0 2px 8px rgba(30,193,149,0.35)" : "none",
    "&:hover": {
        border: `2px solid ${colors.mainSecondary}`,
        background: selected
            ? `linear-gradient(135deg, ${colors.mainSecondary}, #17a882)`
            : "rgba(30,193,149,0.08)",
        transform: "translateY(-1px)",
    },
}));

const ScoreLabels = styled("div")({
    display: "flex",
    justifyContent: "space-between",
    marginTop: "6px",
});

const ScoreLabelText = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.72rem",
    color: "#9ca3af",
    lineHeight: 1.3,
});

// ── Submit button ─────────────────────────────────────────────────────────────

const SubmitBtn = styled(Button)(({ theme }) => ({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "15px 36px",
    textTransform: "none",
    fontSize: "1rem",
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
    color: "#fff",
    boxShadow: "none",
    width: "100%",
    marginTop: "28px",
    "&:hover": {
        background: `linear-gradient(135deg, #0a1a6b 0%, #0d2d85 100%)`,
        boxShadow: "0 4px 14px rgba(4,13,69,0.22)",
    },
    "&:disabled": {
        background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
        opacity: 0.4,
        color: "#fff",
    },
}));

const OptionalBadge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(30,193,149,0.12)",
    color: colors.mainSecondary,
    border: "1px solid rgba(30,193,149,0.25)",
    borderRadius: "100px",
    padding: "3px 11px",
    fontSize: "0.67rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginLeft: "10px",
    verticalAlign: "middle",
});

const PrimaryBtn = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "10px",
    padding: "12px 28px",
    textTransform: "none",
    fontSize: "0.95rem",
    background: `linear-gradient(135deg, ${colors.primary} 0%, #143AA2 100%)`,
    color: "#fff",
    boxShadow: "none",
    "&:hover": {
        background: `linear-gradient(135deg, #0a1a6b 0%, #0d2d85 100%)`,
        boxShadow: "0 4px 14px rgba(4,13,69,0.25)",
    },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type FormData = {
    biggestFrustration: string;
    businessType: string;
    followUpMethods: string[];
    followUpMethodsOther: string;
    paymentLatency: string;
    interestScore: number;
    earlyAccessName: string;
    earlyAccessEmail: string;
};

const INITIAL: FormData = {
    biggestFrustration: "",
    businessType: "",
    followUpMethods: [],
    followUpMethodsOther: "",
    paymentLatency: "",
    interestScore: 0,
    earlyAccessName: "",
    earlyAccessEmail: "",
};

function toggleArr(arr: string[], val: string): string[] {
    return arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];
}

// ── Component ─────────────────────────────────────────────────────────────────

const BusinessSurveyPage: React.FC = () => {
    const { t } = useTranslation("survey");
    const BUSINESS_TYPES = t("opts.businessTypes", { returnObjects: true }) as string[];
    const FOLLOW_UP_METHODS = t("opts.followUpMethods", { returnObjects: true }) as string[];
    const PAYMENT_LATENCY_OPTIONS = t("opts.paymentLatency", { returnObjects: true }) as string[];
    const OTHER_LABEL = t("opts.other");

    const [form, setForm] = useState<FormData>(INITIAL);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const canSubmit = form.biggestFrustration.trim().length > 2;

    const set = (key: keyof FormData, val: any) =>
        setForm((prev) => ({ ...prev, [key]: val }));

    const handleSubmit = async () => {
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API_BASE_URL}/feedback/business-survey`, {
                biggestFrustration: form.biggestFrustration,
                businessType: form.businessType || undefined,
                followUpMethods: form.followUpMethods.length ? form.followUpMethods : undefined,
                followUpMethodsOther: form.followUpMethodsOther || undefined,
                paymentLatency: form.paymentLatency || undefined,
                interestScore: form.interestScore || undefined,
                earlyAccessName: form.earlyAccessName || undefined,
                earlyAccessEmail: form.earlyAccessEmail || undefined,
            });
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            setError(t("error"));
        } finally {
            setSubmitting(false);
        }
    };

    const renderThankYou = () => (
        <Fade in>
            <Box
                sx={{
                    textAlign: "center",
                    py: 7,
                    px: { xs: 3, sm: 5 },
                    background: "#fff",
                    borderRadius: "18px",
                    boxShadow: "0 1px 3px rgba(4,13,69,0.04), 0 4px 20px rgba(4,13,69,0.08)",
                    maxWidth: "560px",
                    mx: "auto",
                    mb: 6,
                }}
            >
                <Box
                    sx={{
                        width: 86,
                        height: 86,
                        borderRadius: "50%",
                        background: "rgba(30,193,149,0.10)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                        border: "2px solid rgba(30,193,149,0.2)",
                    }}
                >
                    <CheckCircleOutline sx={{ fontSize: 46, color: colors.mainSecondary }} />
                </Box>
                <Typography
                    sx={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "clamp(1.6rem, 4vw, 2rem)",
                        fontWeight: 800,
                        color: colors.primary,
                        mb: 1.5,
                        lineHeight: 1.2,
                    }}
                >
                    {t("thankYou.heading")}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.97rem",
                        color: "#4b5563",
                        lineHeight: 1.85,
                        mb: 1,
                        maxWidth: "420px",
                        mx: "auto",
                    }}
                >
                    {t("thankYou.body")}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.91rem",
                        color: "#9ca3af",
                        lineHeight: 1.7,
                        mb: 4,
                    }}
                >
                    {t("thankYou.followUp")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap", mb: 4 }}>
                    {[t("thankYou.tag1"), t("thankYou.tag2"), t("thankYou.tag3")].map((tag) => (
                        <Box
                            key={tag}
                            sx={{
                                background: "rgba(4,13,69,0.05)",
                                borderRadius: "100px",
                                px: 2,
                                py: 0.6,
                                fontSize: "0.82rem",
                                fontFamily: "'Inter', sans-serif",
                                color: colors.primary,
                                fontWeight: 500,
                            }}
                        >
                            {tag}
                        </Box>
                    ))}
                </Box>
                <PrimaryBtn href="/business" sx={{ display: "inline-flex", gap: 1 }}>
                    {t("thankYou.backHome")} <ArrowForward fontSize="small" />
                </PrimaryBtn>
            </Box>
        </Fade>
    );

    return (
        <>
            <SEO title={t("seo.title")} description={t("seo.description")} />
            <PageWrapper>

                {submitted ? (
                    /* ── Thank-you: full-width centered ─────────────────── */
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: { xs: "32px 16px", sm: "48px 24px" },
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {renderThankYou()}
                    </Box>
                ) : (
                    /* ── Side-by-side layout ─────────────────────────────── */
                    <SplitContainer>

                        {/* ── LEFT: Hero pane ─────────────────────────── */}
                        <HeroPane>
                            <BadgeChip>
                                <Bolt sx={{ fontSize: "0.8rem" }} />
                                {t("hero.badge")}
                            </BadgeChip>

                            <HeroTitle>{t("hero.title1")}</HeroTitle>

                            <HeroSub>{t("hero.subtitle")}</HeroSub>

                            <TimeBadge>⏱ {t("hero.timeBadge")}</TimeBadge>
                        </HeroPane>

                        {/* ── RIGHT: Form pane ─────────────────────────── */}
                        <FormPane>
                            <Fade in>
                                <Box>
                                    <Card>

                                        {/* Q1 — Most important, shown first */}
                                        <QuestionLabel>
                                            {t("q1")}<RequiredStar>*</RequiredStar>
                                        </QuestionLabel>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            placeholder={t("q1Placeholder")}
                                            value={form.biggestFrustration}
                                            onChange={(e) => set("biggestFrustration", e.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "12px",
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.95rem",
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: colors.mainSecondary,
                                                    },
                                                },
                                            }}
                                        />

                                        <Divider />

                                        {/* Q2 — Business type */}
                                        <QuestionLabel>{t("q2")}</QuestionLabel>
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ fontFamily: "'Inter', sans-serif", fontSize: "0.92rem" }}>
                                                {t("q2Placeholder")}
                                            </InputLabel>
                                            <Select
                                                value={form.businessType}
                                                label={t("q2Placeholder")}
                                                onChange={(e) => set("businessType", e.target.value)}
                                                sx={{ borderRadius: "11px", fontFamily: "'Inter', sans-serif", fontSize: "0.92rem" }}
                                            >
                                                {BUSINESS_TYPES.map((bt) => (
                                                    <MenuItem key={bt} value={bt} sx={{ fontSize: "0.92rem" }}>
                                                        {bt}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Divider />

                                        {/* Q3 — Follow-up methods */}
                                        <QuestionLabel>{t("q3")}</QuestionLabel>
                                        <HintText>{t("q3Hint")}</HintText>
                                        <Box>
                                            {FOLLOW_UP_METHODS.map((opt) => {
                                                const isSelected = form.followUpMethods.includes(opt);
                                                return (
                                                    <CheckCard
                                                        key={opt}
                                                        selected={isSelected}
                                                        onClick={() => set("followUpMethods", toggleArr(form.followUpMethods, opt))}
                                                        type="button"
                                                    >
                                                        <CheckBox selected={isSelected}>
                                                            {isSelected && (
                                                                <svg viewBox="0 0 12 12" width="11" height="11">
                                                                    <polyline
                                                                        points="2,6 5,9 10,3"
                                                                        fill="none"
                                                                        stroke="#fff"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </CheckBox>
                                                        {opt}
                                                    </CheckCard>
                                                );
                                            })}
                                        </Box>
                                        {form.followUpMethods.includes(OTHER_LABEL) && (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder={t("otherPlaceholder")}
                                                value={form.followUpMethodsOther}
                                                onChange={(e) => set("followUpMethodsOther", e.target.value)}
                                                sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                                            />
                                        )}

                                        <Divider />

                                        {/* Q4 — Payment frequency */}
                                        <QuestionLabel>{t("q4")}</QuestionLabel>
                                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "8px" }}>
                                            {PAYMENT_LATENCY_OPTIONS.map((opt) => (
                                                <OptionCard
                                                    key={opt}
                                                    selected={form.paymentLatency === opt}
                                                    onClick={() => set("paymentLatency", opt)}
                                                    type="button"
                                                    style={{ marginBottom: 0, justifyContent: "center", textAlign: "center" }}
                                                >
                                                    <RadioDot selected={form.paymentLatency === opt} />
                                                    {opt}
                                                </OptionCard>
                                            ))}
                                        </Box>

                                        <Divider />

                                        {/* Q5 — Interest score */}
                                        <QuestionLabel>{t("q5")}</QuestionLabel>
                                        <ScoreGrid>
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                                <ScoreBtn
                                                    key={n}
                                                    selected={form.interestScore === n}
                                                    onClick={() => set("interestScore", n)}
                                                    type="button"
                                                >
                                                    {n}
                                                </ScoreBtn>
                                            ))}
                                        </ScoreGrid>
                                        <ScoreLabels>
                                            <ScoreLabelText>{t("q5ScoreMin")}</ScoreLabelText>
                                            <ScoreLabelText>{t("q5ScoreMax")}</ScoreLabelText>
                                        </ScoreLabels>

                                    </Card>

                                    {/* ── Optional early-access card ──────── */}
                                    <OptionalCard>
                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Montserrat', sans-serif",
                                                    fontWeight: 800,
                                                    fontSize: "1.02rem",
                                                    color: "#ffffff",
                                                }}
                                            >
                                                {t("optionalTitle")}
                                            </Typography>
                                            <OptionalBadge>{t("optionalBadge")}</OptionalBadge>
                                        </Box>
                                        <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={t("nameLabel")}
                                                placeholder={t("namePlaceholder")}
                                                value={form.earlyAccessName}
                                                onChange={(e) => set("earlyAccessName", e.target.value)}
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                                            />
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="email"
                                                label={t("emailLabel")}
                                                placeholder={t("emailPlaceholder")}
                                                value={form.earlyAccessEmail}
                                                onChange={(e) => set("earlyAccessEmail", e.target.value)}
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                                            />
                                        </Box>
                                    </OptionalCard>

                                    {error && (
                                        <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
                                            {error}
                                        </Alert>
                                    )}

                                    <SubmitBtn
                                        disabled={!canSubmit || submitting}
                                        onClick={handleSubmit}
                                        endIcon={
                                            submitting
                                                ? <CircularProgress size={16} color="inherit" />
                                                : <AutoAwesome fontSize="small" />
                                        }
                                    >
                                        {submitting ? t("submittingBtn") : t("submitBtn")}
                                    </SubmitBtn>

                                </Box>
                            </Fade>
                        </FormPane>

                    </SplitContainer>
                )}

                <Footer />
            </PageWrapper>
        </>
    );
};

export default BusinessSurveyPage;
